import { memo, useEffect, useRef, useState } from 'react';
import type { Attachment } from '../api/client';
import { ImagePreviewModal } from './ImagePreviewModal';

interface Props {
  attachment: Attachment;
  /** Returns the presigned download URL for this attachment. */
  getDownloadUrl: (attachmentId: string) => Promise<string>;
  /** If true, renders as a compact row (for the aggregated Attachments tab). */
  compact?: boolean;
}

/**
 * Renders a single attachment.
 * - Images: clickable thumbnail (lazy-loaded presigned URL). Click opens full size in new tab.
 * - Other files: file icon row with a Download button.
 */
export const AttachmentItem = memo(
  function AttachmentItem({ attachment, getDownloadUrl, compact = false }: Props): JSX.Element {
    const isImage = attachment.contentType.startsWith('image/');
    // DEBUG: count renders (if memo is working this should be rare)
    console.log('[AttachmentItem] RENDER', attachment.attachmentId);
    const [imgUrl, setImgUrl] = useState<string | null>(null);
    const [imgLoading, setImgLoading] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    // DEBUG: detect unexpected mounts/unmounts
    useEffect(() => {
      console.log('[AttachmentItem] MOUNT', attachment.attachmentId);
      return () => console.log('[AttachmentItem] UNMOUNT', attachment.attachmentId);
    }, [attachment.attachmentId]);

    // Keep a ref to the latest getDownloadUrl so neither the effect nor openFile
    // need it as a dependency — the function identity changes on every parent render
    // (e.g. while the user is typing a comment) even though it is semantically the
    // same call.
    const getDownloadUrlRef = useRef(getDownloadUrl);
    getDownloadUrlRef.current = getDownloadUrl;

    useEffect(() => {
      if (!isImage) return;
      console.log('[AttachmentItem] FETCHING url for', attachment.attachmentId);
      setImgLoading(true);
      getDownloadUrlRef.current(attachment.attachmentId)
        .then(setImgUrl)
        .catch(() => setImgUrl(null))
        .finally(() => setImgLoading(false));
    }, [attachment.attachmentId, isImage]);

    async function openFile() {
      setActionBusy(true);
      try {
        const url = await getDownloadUrlRef.current(attachment.attachmentId);
        window.open(url, '_blank', 'noopener,noreferrer');
      } finally {
        setActionBusy(false);
      }
    }

    if (isImage) {
      return (
        <>
          <button
            className="block focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
            title={attachment.filename}
            onClick={() => imgUrl && setPreviewOpen(true)}
            disabled={!imgUrl || imgLoading}
          >
            {imgLoading || !imgUrl ? (
              <div className="w-20 h-20 bg-gray-800 animate-pulse rounded flex items-center justify-center text-xs text-gray-500">
                {imgLoading ? '…' : '?'}
              </div>
            ) : (
              <img
                src={imgUrl}
                alt={attachment.filename}
                className="w-20 h-20 object-cover rounded border border-gray-700 hover:opacity-90 transition-opacity"
              />
            )}
            {compact && (
              <span className="block text-xs text-gray-500 mt-0.5 max-w-[80px] truncate">
                {attachment.filename}
              </span>
            )}
          </button>
          {previewOpen && imgUrl && (
            <ImagePreviewModal
              src={imgUrl}
              filename={attachment.filename}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </>
      );
    }

    if (compact) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <FileIcon contentType={attachment.contentType} />
          <div className="flex-1 min-w-0">
            <div className="truncate text-gray-200">{attachment.filename}</div>
            <div className="text-xs text-gray-500">{formatBytes(attachment.size)}</div>
          </div>
          <button
            className="btn-secondary text-xs py-0.5 px-2 shrink-0"
            onClick={openFile}
            disabled={actionBusy}
          >
            {actionBusy ? '…' : 'View'}
          </button>
        </div>
      );
    }

    return (
      <button
        className="flex items-center gap-2 text-sm hover:bg-gray-800 rounded px-2 py-1 -mx-2 transition-colors w-full text-left"
        onClick={openFile}
        disabled={actionBusy}
        title={`Download ${attachment.filename}`}
      >
        <FileIcon contentType={attachment.contentType} />
        <span className="flex-1 truncate text-gray-200">{attachment.filename}</span>
        <span className="text-xs text-gray-500 shrink-0">{formatBytes(attachment.size)}</span>
      </button>
    );
  },
  // Compare by attachmentId (stable primitive) rather than object reference so
  // that parent-created spread objects (e.g. allAttachments map) don't cause
  // spurious re-renders. getDownloadUrl is excluded — the internal ref tracks
  // the latest version.
  (prev, next) =>
    prev.attachment.attachmentId === next.attachment.attachmentId &&
    prev.compact === next.compact,
);

function FileIcon({ contentType }: { contentType: string }): JSX.Element {
  const isPdf = contentType === 'application/pdf';
  return (
    <span
      className={`text-xs font-bold px-1 py-0.5 rounded ${
        isPdf ? 'bg-red-900/60 text-red-300 border border-red-700/50' : 'bg-gray-800 text-gray-400 border border-gray-700'
      }`}
    >
      {isPdf ? 'PDF' : 'FILE'}
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
